import { useEffect } from 'react';
import { trackEvent } from '@/lib/analytics';

/**
 * Global delegated click tracker. Catches conversion clicks anywhere in the
 * DOM without having to wire onClick into every component:
 *
 *  - tel: links               -> phone_click
 *  - wa.me / whatsapp links   -> whatsapp_click
 *  - links to /contact /booking with primary CTA copy -> cta_click
 *
 * Uses event delegation on the document so it covers new routes too.
 */
const CTA_PATTERNS = [
  /zaka[zž]i/i,
  /book/i,
  /записа/i,
  /termin/i,
  /pregled/i,
  /konsultacij/i,
];

export function useConversionTracking() {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      const text = (anchor.textContent || '').trim().slice(0, 80);

      if (href.startsWith('tel:')) {
        trackEvent('phone_click', { label: href.replace('tel:', '') });
        return;
      }
      if (href.includes('wa.me') || href.includes('whatsapp')) {
        trackEvent('whatsapp_click', { label: 'whatsapp_float_or_link' });
        return;
      }
      const isInternalCtaTarget =
        href === '/contact' ||
        href === '/booking' ||
        href.endsWith('/contact') ||
        href.endsWith('/booking');
      if (isInternalCtaTarget && CTA_PATTERNS.some((p) => p.test(text))) {
        trackEvent('cta_click', { label: text || href });
      }
    };

    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true } as any);
  }, []);
}
